import {
    createAsyncThunk,
    createSlice,
} from "@reduxjs/toolkit";

import { toast } from 'react-toastify';
import { keys } from 'lodash';
import axios from "../utils/axios";

import {
    IError,
    ILoginRequest,
    IAuthState,
    IUser,
    IUserProfilePicUpdatePayload,
    IProfilePicUpdateResponse,
    TSidebarCategories,
    ISwitchAccount,
    ILoginResponse,
    IRegisterRequest,
} from "../utils/auth.types";

import { IAccount, IResource } from "../utils/account.types";
import modulePermissionService, { UserModulePermissions } from "../services/ModulePermissionService";
import { collectClientInfo } from "../utils/deviceInfo";

/**
 * User login async thunk.
 */
export const login = createAsyncThunk<
        IUser,
        ILoginRequest,
        {
            rejectValue: IError,
        }
    >(
        'auth/login',
        async({ email, password, otp }: ILoginRequest, thunkApi) => {
            try {
                // Device details for the Audit Trail's login entry.
                const client = await collectClientInfo();
                const response = await axios.post('/api/v1/login', { email, password, otp, client });

                const { data } = response;

                // MFA is on: no session yet, the caller has to clear the challenge first.
                if (data?.mfaRequired) {
                    return data as any;
                }

                if (response.status === 200) {
                    const { token, user } = data;

                    localStorage.setItem('token', token);
                    localStorage.setItem('user', JSON.stringify(user));
                    localStorage.setItem('selectedAccount', JSON.stringify(user.accounts[0]));

                    return user;
                } else {
                    return thunkApi.rejectWithValue(data.error);
                }
            } catch (error: any) {
                return thunkApi.rejectWithValue(error.response.data);
            }
        }
)

/**
 * Confirms the MFA code and, on success, starts the session the same way login does.
 */
export const verifyMfa = createAsyncThunk<
        IUser,
        { mfaToken: string; otp: string },
        {
            rejectValue: IError,
        }
    >(
        'auth/verifyMfa',
        async({ mfaToken, otp }, thunkApi) => {
            try {
                const response = await axios.post('/api/v1/mfa/verify', { mfaToken, otp });
                const { token, user } = response.data;

                localStorage.setItem('token', token);
                localStorage.setItem('user', JSON.stringify(user));
                localStorage.setItem('selectedAccount', JSON.stringify(user.accounts[0]));

                return user;
            } catch (error: any) {
                return thunkApi.rejectWithValue(error.response.data);
            }
        }
)

/**
 * Register async Thunk.
*/
export const register = createAsyncThunk<
        IUser,
        IRegisterRequest,
        {
            rejectValue: IError,
        }
    >(
        'auth/register',
        async({ name, email, phone, password }: IRegisterRequest, thunkApi) => {
            try {
                const response = await axios.post('/api/v1/register', { name, email, phone, password });

                const { data } = response;

                if (response.status === 200) {
                    const {token,  user } = data;
                    localStorage.setItem('user', JSON.stringify(user));
                    localStorage.setItem('token', token);
                    return user;
                } else {
                    return thunkApi.rejectWithValue(data.error);
                }
            } catch (error: any) {
                return thunkApi.rejectWithValue(error.response.data);
            }
        }
)


/**
 * Update User profile pic async Thunk.
*/
export const updateProfilePic = createAsyncThunk(
    'auth/updateProfilePic',
    async({ id, profilePic }: IUserProfilePicUpdatePayload): Promise<IProfilePicUpdateResponse> => {
        const response = await axios.put(`/api/v1/user/${id}`, profilePic);

        return response.data;
    }
);

/**
 * Switch Account async Thunk.
*/
export const switchAccount = createAsyncThunk(
    'auth/switchAccount',
    async({accountId, userId}: ISwitchAccount): Promise<ILoginResponse> => {
        const response = await axios.post(`/api/v1/switchaccount/${accountId}`, null, { params: { userId } });

        response.data['selectedAccountId'] = accountId;

        return response.data;
    }
);

export const sendOtp = createAsyncThunk(
    'auth/sendOtp',
    async({email}: any): Promise<any> => {
        const response = await axios.post(`/api/v1/user/otp`, { email });
        return response.data;
    }
);

/**
 * Load User Permissions async thunk - fetches permissions from new module system
 */
export const loadUserPermissions = createAsyncThunk(
    'auth/loadUserPermissions',
    async(userId: string, thunkApi) => {
        try {
            console.log('🚀 loadUserPermissions called for userId:', userId);
            const modulePermissions = await modulePermissionService.getUserModulePermissions(userId);
            console.log('📥 Raw module permissions received:', modulePermissions);
            
            const legacyPermissions = modulePermissionService.convertToLegacyFormat(modulePermissions);
            const sidebarCategories = modulePermissionService.getSidebarCategories(modulePermissions);
            
            console.log('🔄 Processed legacyPermissions:', legacyPermissions);
            console.log('🔄 Processed sidebarCategories:', sidebarCategories);
            
            return {
                permissions: legacyPermissions,
                sidebarCategories,
                modulePermissions
            };
        } catch (error: any) {
            console.error('❌ loadUserPermissions failed:', error);
            return thunkApi.rejectWithValue(error.response?.data || error.message);
        }
    }
);

/**
 * Load the account's subscription (plan status, allowed modules, limits). Without
 * usage it is cheap enough to run on every page change.
 */
export const loadPlan = createAsyncThunk(
    'auth/loadPlan',
    async (withUsage: boolean, thunkApi) => {
        try {
            const response = await axios.get(`/api/v1/subscription/current${withUsage ? '' : '?usage=false'}`);
            return response.data;
        } catch (error: any) {
            return thunkApi.rejectWithValue(error.response?.data || error.message);
        }
    }
);

// Read token and user from local storage to set initial state of user on page reload.
const token = localStorage.getItem('token');
const user = localStorage.getItem('user');
const selectedAccount = localStorage.getItem('selectedAccount');

/**
 * Auth Slice initial data.
 */
const authSliceInitialState: IAuthState = {
    user: user ? JSON.parse(user) : {},
    isLoggedIn: token ? true: false,
    isLoading: false,
    isError: false,
    errorMessage: '',
    permissions: [],
    sidebarCategories: {},
    modulePermissions: null,
    plan: null,
    switchingAccount: false,
    selectedAccount: selectedAccount ? JSON.parse(selectedAccount) : {},
    isSelfRegistered: false,
};
if (user && selectedAccount) {
    // Initialize permissions on app startup (handled async in component)
    // setUserPermissionsAndSidebarOptions will be called in useEffect of App component
}

/**
 * Auth Slice.
 */
export const authSlice = createSlice({
    name: 'auth',
    initialState: authSliceInitialState,
    reducers: {
        clearState: (state) => {
            state.isError = false;
            state.isLoading = false;
            state.errorMessage = '';
      
            return state;
        },
        logout: (state) => {
            state.isLoggedIn = false;
            // We have taken care on this 'logout' action in rootReducer to clear the complete Redux store's state. 
        }
    },
    extraReducers: (builder) => {
        builder
        .addCase(login.pending, (state) => {
            // When user login request is still in progress.
            state.isLoading = true;
        })
        .addCase(login.fulfilled, (state, action) => {
            // Password was right but a second factor is still owed — stay signed out.
            if ((action.payload as any)?.mfaRequired) {
                state.mfaChallenge = action.payload as any;
                state.isLoggedIn = false;
                state.isLoading = false;
                state.isError = false;
                state.errorMessage = '';
                return;
            }
            state.mfaChallenge = undefined;
            // When user login request is complete.
            const { accounts } = action.payload;

            state.user = action.payload;

            // set first account as selected account when user logs in.
            state.selectedAccount = accounts[0];
            state.isLoggedIn = true;
            state.isLoading = false
            state.isError = false;
            state.errorMessage = '';

            // setUserPermissionsAndSidebarOptions(state, state.selectedAccount);
            // Permissions will be loaded via loadUserPermissions thunk dispatch in component
        })
        .addCase(login.rejected, (state, action) => {
            // When user login request is rejected.
            state.isLoggedIn = false;
            state.isLoading = false;
            state.isError = true;
        })
        .addCase(verifyMfa.pending, (state) => {
            state.isLoading = true;
        })
        .addCase(verifyMfa.fulfilled, (state, action) => {
            state.mfaChallenge = undefined;
            state.user = action.payload;
            state.selectedAccount = action.payload.accounts[0];
            state.isLoggedIn = true;
            state.isLoading = false;
            state.isError = false;
            state.errorMessage = '';
        })
        .addCase(verifyMfa.rejected, (state, action) => {
            state.isLoggedIn = false;
            state.isLoading = false;
            state.isError = true;
            state.errorMessage = (action.payload as any)?.message || 'Invalid OTP.';
        })
        // Update User Profile picture
        .addCase(updateProfilePic.pending, (state, action) => {
            state.isLoading = true;
        })
        .addCase(updateProfilePic.fulfilled, (state, action) => {
            state.isLoading = false
            state.isError = false;
            state.user.profilePic = action.payload.user.profilePic;

            toast.success(action.payload.message);
        })
        .addCase(updateProfilePic.rejected, (state, action) => {
            state.isLoading = false;
            state.isError = true;
        })
        // switch account
        .addCase(switchAccount.pending, (state, action) => {
            state.switchingAccount = true;
        })
        .addCase(switchAccount.fulfilled, (state, action) => {
            // When switch account request is complete.
            const { token, user, selectedAccountId } = action.payload;

            state.user = user;

            // find the select account to set the permission and the sidebar options for the selected account.
            const selectedAccount = state.user.accounts.find(account => account._id === selectedAccountId);

            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(user));
            localStorage.setItem('selectedAccount', JSON.stringify(selectedAccount));
            
            state.selectedAccount = selectedAccount ?? {} as IAccount;
            state.isLoggedIn = true;
            state.switchingAccount = false
            state.isError = false;
            state.errorMessage = '';
            state.isSelfRegistered = false;

            // Permissions will be loaded via loadUserPermissions thunk dispatch in component
        })
        .addCase(switchAccount.rejected, (state, action) => {
            // When switch account request is rejected.
            state.switchingAccount = false;
        })
        .addCase(register.pending, (state, action) => {
            state.isLoading = true;
        })
        .addCase(register.fulfilled, (state, action) => {
            state.user = action.payload;
            state.isLoggedIn = true;
            state.isLoading = false
            state.isError = false;
            state.errorMessage = '';        
            state.isSelfRegistered = selectedAccount ? false :  true;
        })
        .addCase(register.rejected, (state) => {
            state.isLoggedIn = false;
            state.isLoading = false;
            state.isError = true;
        })
        // Load user permissions from new module system
        .addCase(loadUserPermissions.pending, (state) => {
            // Permissions are loading
        })
        .addCase(loadUserPermissions.fulfilled, (state, action) => {
            // Update permissions from new module system
            console.log('✅ loadUserPermissions.fulfilled - updating Redux state');
            console.log('📝 Setting permissions:', action.payload.permissions);
            console.log('📝 Setting sidebarCategories:', action.payload.sidebarCategories);
            
            state.permissions = action.payload.permissions;
            state.sidebarCategories = action.payload.sidebarCategories;
            state.modulePermissions = action.payload.modulePermissions;

            console.log('✅ Redux state updated successfully');
        })
        .addCase(loadUserPermissions.rejected, (state, action) => {
            // Handle permission loading failure
            console.error('Failed to load user permissions:', action.payload);
        })
        .addCase(loadPlan.fulfilled, (state, action) => {
            // a status-only check keeps the last known usage
            const usage = Object.keys(action.payload?.usage || {}).length ? action.payload.usage : state.plan?.usage;
            state.plan = { ...action.payload, usage };
        })
    }
});



export const { clearState, logout } = authSlice.actions;

export default authSlice.reducer;